import { useEffect, useMemo, useState } from "react";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { getAuftragAppAngebot, type AppAngebotBundle } from "@/lib/projectOverview";
import { formatHours } from "@/pages/projekte/hoursUsage";
import { KalkStundenOverview } from "@/projectOverview/KalkStundenOverview";
import { OfferPositionsView } from "@/projectOverview/sections/OfferPositionsView";

function resolveOfferList(bundle: AppAngebotBundle | null): AppAngebotBundle[] {
  if (!bundle) return [];
  if (Array.isArray(bundle.angebote) && bundle.angebote.length > 0) {
    return bundle.angebote.filter((item) => item?.dokument_id != null);
  }
  return bundle.dokument_id != null ? [bundle] : [];
}

/** Büro-Tab: App-Angebote mit Positionen und Preisen. */
export function ProjectAngebotTab({ orderId }: { orderId: number }) {
  const [bundle, setBundle] = useState<AppAngebotBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void getAuftragAppAngebot(orderId)
      .then((data) => {
        if (!cancelled) setBundle(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setBundle(null);
          setError(err instanceof Error ? err.message : "Angebot konnte nicht geladen werden.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const offers = useMemo(() => resolveOfferList(bundle), [bundle]);
  const kalkStunden = Array.isArray(bundle?.kalk_stunden) ? bundle.kalk_stunden : [];
  const nachweisStunden =
    bundle?.nachweis_stunden != null && Number(bundle.nachweis_stunden) > 0
      ? Number(bundle.nachweis_stunden)
      : null;
  const offerCount = offers.length;

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return <Alert variant="error">{error}</Alert>;
  }

  if (!offerCount) {
    return (
      <div className="rounded-ios-lg border border-separator bg-card p-4 sm:p-5">
        <h2 className="mb-2 text-base font-semibold text-label">App-Angebot</h2>
        <p className="text-sm text-label-secondary">
          Für dieses Projekt liegt noch kein App-Angebot vor.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {offerCount > 1 && (kalkStunden.length > 0 || nachweisStunden != null) ? (
        <div className="rounded-ios-lg border border-separator bg-card p-4 sm:p-5 space-y-3">
          {kalkStunden.length > 0 ? (
            <KalkStundenOverview
              items={kalkStunden}
              title="Kalkulierte Festpreis-Stunden (alle Angebote)"
              heading="section"
            />
          ) : null}
          {nachweisStunden != null ? (
            <div className="flex justify-between gap-3 text-sm text-label">
              <span className="font-semibold text-label">
                Angebotene Nachweisstunden (alle Angebote)
              </span>
              <span className="tabular-nums font-medium">{formatHours(nachweisStunden)}</span>
            </div>
          ) : null}
        </div>
      ) : null}

      {offers.map((offer, idx) => (
        <div
          key={offer.dokument_id ?? idx}
          className="rounded-ios-lg border border-separator bg-card p-4 sm:p-5"
        >
          <h2 className="mb-4 text-base font-semibold text-label">
            {offerCount > 1
              ? `App-Angebot ${idx + 1} von ${offerCount}`
              : "App-Angebot"}
          </h2>
          <OfferPositionsView
            bundle={offer}
            emptyText="Für dieses Projekt liegt noch kein App-Angebot vor."
            showKalkStunden={offerCount === 1 || (offer.kalk_stunden?.length ?? 0) > 0}
            kalkStundenLabel={
              offerCount > 1
                ? "Festpreis-Stunden (dieses Angebot)"
                : "Kalkulierte Festpreis-Stunden"
            }
          />
        </div>
      ))}
    </div>
  );
}
